import { formatDurationToHrMin } from "@utils/utils";
import React from "react";

import { AnalyticsStatCard } from "@fronterahealth/frontera-ui-components";

import { RbtSessionAnalyticsVideosDataType } from "@api/graphql/types-and-hooks";
import { CardContainer } from "@pages/VideoAnalytics/components/CardContainer";
import {
  ProgressInputData,
  RBT_ANALYTICS_TITLES,
  STATUS_MAPPING,
  calculatePercentageChange,
  transformToProgressData,
} from "@pages/VideoAnalytics/helpers";

interface RecordingMetricsProps {
  currentData: RbtSessionAnalyticsVideosDataType | null | undefined;
  previousPeriodData: RbtSessionAnalyticsVideosDataType | null | undefined;
  isFetching: boolean;
  setIsVideosRecordedDialogOpen: (val: boolean) => void;
  setIsVideoRecordingDurationDialogOpen: (val: boolean) => void;
  setIsAvgVideoUploadTimeDialogOpen: (val: boolean) => void;
}

export const RecordingMetrics: React.FC<RecordingMetricsProps> = ({
  currentData,
  previousPeriodData,
  isFetching,
  setIsVideosRecordedDialogOpen,
  setIsVideoRecordingDurationDialogOpen,
  setIsAvgVideoUploadTimeDialogOpen,
}) => {
  const videoStatusData = currentData?.sessionsStateDistribution as ProgressInputData;
  const videoStatusProgressData = currentData?.sessionsStateDistribution
    ? transformToProgressData(
        {
          ...videoStatusData,
          processing: (videoStatusData.scheduled ?? 0) + (videoStatusData.processing ?? 0),
        },
        STATUS_MAPPING,
      )
    : undefined;
  return (
    <CardContainer title="Recording Metrics">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2">
        <AnalyticsStatCard
          title={RBT_ANALYTICS_TITLES.TOTAL_VIDEOS_RECORDED}
          value={`${currentData?.totalSessionsRecorded} videos`}
          previousValue={
            previousPeriodData ? `${previousPeriodData?.totalSessionsRecorded ?? "N/A"} videos` : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.totalSessionsRecorded ?? 0),
                  Number(previousPeriodData?.totalSessionsRecorded ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.totalSessionsRecorded ?? 0),
                  Number(previousPeriodData?.totalSessionsRecorded ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          breakdownData={videoStatusProgressData}
          showGraphLink
          onGraphClick={() => setIsVideosRecordedDialogOpen(true)}
        />

        <AnalyticsStatCard
          title={RBT_ANALYTICS_TITLES.VIDEO_RECORDING_DURATION}
          value={`${formatDurationToHrMin(currentData?.videoRecordingsDurationInSeconds ?? 0)}`}
          previousValue={
            previousPeriodData
              ? `${previousPeriodData ? formatDurationToHrMin(previousPeriodData?.videoRecordingsDurationInSeconds ?? 0) : "N/A"}`
              : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.videoRecordingsDurationInSeconds ?? 0),
                  Number(previousPeriodData?.videoRecordingsDurationInSeconds ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.videoRecordingsDurationInSeconds ?? 0),
                  Number(previousPeriodData?.videoRecordingsDurationInSeconds ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => setIsVideoRecordingDurationDialogOpen(true)}
        />
        <AnalyticsStatCard
          title={RBT_ANALYTICS_TITLES.AVG_VIDEO_UPLOAD_TIME}
          value={`${formatDurationToHrMin(currentData?.averageVideoUploadTime ?? 0)}`}
          previousValue={
            previousPeriodData
              ? `${previousPeriodData ? formatDurationToHrMin(previousPeriodData?.averageVideoUploadTime ?? 0) : "N/A"}`
              : undefined
          }
          percentageChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageVideoUploadTime ?? 0),
                  Number(previousPeriodData?.averageVideoUploadTime ?? 0),
                ).change
              : undefined
          }
          isPositiveChange={
            previousPeriodData
              ? calculatePercentageChange(
                  Number(currentData?.averageVideoUploadTime ?? 0),
                  Number(previousPeriodData?.averageVideoUploadTime ?? 0),
                ).isPositive
              : undefined
          }
          loading={isFetching}
          showGraphLink
          onGraphClick={() => setIsAvgVideoUploadTimeDialogOpen(true)}
        />
      </div>
    </CardContainer>
  );
};
